import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE_URL, OG_IMAGE, SERVICES, SERVICE_AREAS, getServiceContent, getAreaContent } from "../data/business";

// Centralised, per-route SEO. Rendered once inside <BrowserRouter>, it derives
// a unique <title>, meta description, canonical URL, and Open Graph / Twitter
// tags from the current path — including dynamic /services/:slug and
// /service-areas/:slug routes. react-snap serialises this output into the
// prerendered HTML so every route ships correct, crawlable metadata.

const SUFFIX = " | Drain Sights";

const STATIC_META = {
  "/": {
    title: "Drain Sights | Drain Cleaning & Sewer Service in Baltimore, MD",
    description:
      "Baltimore-metro drain & sewer specialist: drain cleaning, sewer camera inspection, hydro-jetting, sewer line locating & sump pump service. Licensed & insured, 24/7 emergency service.",
  },
  "/about": {
    title: "About Drain Sights — Baltimore Drain & Sewer Specialists" + "",
    description:
      "Meet Drain Sights: 30+ years of drain, sewer & HVAC experience serving the Baltimore and DC metros. Licensed, insured, and available 24/7.",
  },
  "/services": {
    title: "Drain & Sewer Services in Baltimore, MD" + SUFFIX,
    description:
      "Drain cleaning, sewer camera inspection, hydro-jetting, pipe descaling, sewer & water line replacement, sump pumps and more across the Baltimore & DC metros.",
  },
  "/service-areas": {
    title: "Service Areas — Baltimore & DC Metro" + SUFFIX,
    description:
      "Drain Sights serves two Maryland metros — the Baltimore area and the Temple Hills / DC area. See all the cities we cover for drain & sewer service.",
  },
  "/gallery": {
    title: "Recent Drain & Sewer Projects" + SUFFIX,
    description:
      "A look at recent drain cleaning, sewer camera inspection, hydro-jetting and sewer line work completed across the Baltimore and DC metros.",
  },
  "/reviews": {
    title: "Customer Reviews" + SUFFIX,
    description:
      "See what homeowners across the Baltimore and DC metros say about Drain Sights' drain & sewer service.",
  },
  "/write-a-review": {
    title: "Write a Review" + SUFFIX,
    description: "Share your experience with Drain Sights drain & sewer service.",
  },
  "/faq": {
    title: "Drain & Sewer FAQs" + SUFFIX,
    description:
      "Answers to common questions about drain cleaning, sewer camera inspection, hydro-jetting, pricing, service areas and scheduling with Drain Sights.",
  },
  "/contact": {
    title: "Contact Drain Sights — Baltimore, MD" + "",
    description:
      "Call (240) 332-5647 or request a free quote. Drain Sights answers 24/7 for drain & sewer help across the Baltimore and DC metros.",
  },
  "/discount": {
    title: "10% Off Your Service — Limited-Time Offer" + SUFFIX,
    description:
      "Save 10% on drain, sewer & waterproofing service from Drain Sights — a limited-time offer for the Baltimore & DC metros. Free estimates, 24/7 emergency service.",
  },
  "/terms": {
    title: "Terms of Service" + SUFFIX,
    description: "Terms of service for Drain Sights.",
  },
  "/privacy": {
    title: "Privacy Policy" + SUFFIX,
    description: "Privacy policy for Drain Sights.",
  },
};

function normalise(pathname) {
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
}

function getMeta(pathname) {
  const path = normalise(pathname);

  if (STATIC_META[path]) return STATIC_META[path];

  if (path.startsWith("/services/")) {
    const slug = path.split("/")[2];
    const svc = SERVICES.find((s) => s.slug === slug);
    if (svc) {
      const content = getServiceContent(slug);
      return {
        title: content?.metaTitle ? content.metaTitle : `${svc.name} in Baltimore, MD${SUFFIX}`,
        description: content?.metaDescription || `${svc.short} Drain Sights provides ${svc.name.toLowerCase()} across the Baltimore and DC metros — licensed, insured, 24/7.`,
      };
    }
  }

  if (path.startsWith("/service-areas/")) {
    const slug = path.split("/")[2];
    const area = SERVICE_AREAS.find((a) => a.slug === slug);
    if (area) {
      const content = getAreaContent(slug);
      return {
        title: content?.metaTitle ? `${content.metaTitle}` : `Drain & Sewer Service in ${area.city}, ${area.state}${SUFFIX}`,
        description: content?.metaDescription || `${area.blurb} Drain cleaning, sewer camera inspection, hydro-jetting and sewer line work for ${area.city}, ${area.state}. Call (240) 332-5647.`,
      };
    }
  }

  // Fallback (e.g. 404)
  return {
    title: "Page Not Found" + SUFFIX,
    description:
      "Baltimore-metro drain & sewer specialist. Drain cleaning, sewer camera inspection, hydro-jetting and more.",
  };
}

export default function SeoManager() {
  const { pathname } = useLocation();
  const meta = getMeta(pathname);
  const path = normalise(pathname);
  const canonical = `${SITE_URL}${path === "/" ? "/" : path}`;

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={OG_IMAGE} />
    </Helmet>
  );
}
